import { useAuth } from '@opendoor/auth-fe/context';
import { Box, getCSSVariable } from '@opendoor/bricks-next';
import type { Entry } from 'contentful';
import { getFirstQueryParam } from 'helpers/queryParams';
import { useRouter } from 'next/router';

import type { Awaited, EntryComponent, LoaderFn } from '../../cms/entries/entries';
import type {
  ILandingPageFields,
  ILpComponentHeroV2,
  ILpComponentHeroV2WithMarkers,
} from '../../declarations/contentful';
import { getMarketDisplayNameForHeroV2 } from '../../helpers/markets';
import {
  generatePresetPictureSet,
  generatePresetPictureSetProps,
} from '../shared/performance/pictures';
import type { PictureSourceSpec } from '../shared/performance/pictures';
import {
  FULLBLEED_HERO_IMAGE_URL,
  GLASS_HEADER_HEIGHT,
  isTreatmentAnalyticsId,
} from './shared/header/glass/constants';
import { HERO_FULLBLEED_IMAGE_PROPS } from './shared/hero/heroConstants';
import { HeroImages } from './shared/hero/HeroImages';
import {
  getStickyCTAFromRoot,
  HeroProvider,
  preloadHeroImages,
  useStickyCTA,
} from './shared/hero/HeroShared';
import { HeroTitleAndAddress } from './shared/hero/HeroTitleAndAddress';
import type { HeroV2CustomizationProps } from './shared/hero/heroTypes';
import { PressBox } from './shared/hero/PressBox';
import { SocialProofBox } from './SocialProofBox';
import { StickyCTAUtility } from './StickyCTAUtility/StickyCTAUtility';

import styles from './HeroV2.module.css';

const FULLBLEED_HERO_FALLBACK_COLOR = '#2D1D12';

const FullBleedHero = ({
  fields,
  headline,
  customization,
  analyticsPrefix,
  hideStickyCta,
  showStickyCTAUtility,
  addressEntryRef,
}: {
  fields: ILpComponentHeroV2['fields'];
  headline: string;
  customization?: HeroV2CustomizationProps;
  analyticsPrefix: string;
  hideStickyCta: boolean;
  showStickyCTAUtility: boolean;
  addressEntryRef: (node?: Element | null | undefined) => void;
}) => {
  const variant = customization?.variant || 'default';
  const imageUrl = FULLBLEED_HERO_IMAGE_URL;

  return (
    <HeroProvider variant={variant} analyticsPrefix={analyticsPrefix}>
      <Box
        data-hero
        analyticsName={
          customization?.analyticsName || `cosmos-landing-page-hero-v2-${fields?.analyticsId}`
        }
        trackImpression
        inViewThreshold={0.3}
        position="relative"
        maxWidth="$432"
        mx="auto"
        pt={GLASS_HEADER_HEIGHT}
        borderBottomLeftRadius="$6x"
        borderBottomRightRadius="$6x"
        minHeight="$100"
        height={500}
        style={{ backgroundColor: FULLBLEED_HERO_FALLBACK_COLOR }}
        $largerThanSM={{ minHeight: '$115' }}
        $largerThanMD={{
          minHeight: '$125',
          borderBottomLeftRadius: '$9x',
          borderBottomRightRadius: '$9x',
        }}
      >
        {!hideStickyCta && (
          <StickyCTAUtility analyticsId={fields?.analyticsId} isVisible={showStickyCTAUtility} />
        )}

        <Box
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
          overflow="hidden"
          borderBottomLeftRadius="$6x"
          borderBottomRightRadius="$6x"
          $largerThanMD={{ borderBottomLeftRadius: '$9x', borderBottomRightRadius: '$9x' }}
        >
          {imageUrl && (
            <Box
              position="absolute"
              top={0}
              left={0}
              width="100%"
              height="100%"
              className={styles.heroImageWrapper}
            >
              {generatePresetPictureSet({
                ...HERO_FULLBLEED_IMAGE_PROPS,
                photoUrl: imageUrl,
                objectFit: 'cover',
                preload: false,
              })}
            </Box>
          )}

          <Box
            position="absolute"
            top={0}
            left={0}
            width="66%"
            height="100%"
            className={styles.heroLeftGradient}
          />

          <Box
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="$23"
            zIndex={1}
            className={styles.heroTopGradient}
          />
        </Box>

        <Box
          position="relative"
          zIndex={2}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          width="100%"
          height="100%"
          px="$5"
          py="$10"
          maxWidth="$328"
          mx="auto"
          style={{ color: getCSSVariable('$contentInversePrimary', 'color') }}
          minHeight={400 - GLASS_HEADER_HEIGHT}
          $largerThanSM={{
            pt: '$25',
            minHeight: 460 - GLASS_HEADER_HEIGHT,
            alignItems: 'flex-start',
          }}
          $largerThanMD={{ pt: '$14', minHeight: 500 - GLASS_HEADER_HEIGHT }}
        >
          <Box maxWidth="80%" $largerThanSM={{ maxWidth: '50%' }}>
            <HeroTitleAndAddress
              analyticsId={fields?.analyticsId}
              title={headline}
              subtitle={fields?.subtitle}
              showAddressInput={fields?.showAddressInputCta}
              showSocialSection={false}
              textColor="$contentInversePrimary"
              renderDesktopInput={customization?.renderDesktopInput}
              renderMobileInput={customization?.renderMobileInput}
              additionalAddressContent={customization?.additionalAddressContent}
              addressEntryRef={addressEntryRef}
              addressCtaText={fields?.addressCtaText}
              addressCtaTextSmall={fields?.addressCtaTextSmall}
              addressCtaPlaceholderText={fields?.addressCtaPlaceholderText}
              addressCtaPlaceholderTextSmall={fields?.addressCtaPlaceholderTextSmall}
              showCtaButtonsContainer={fields?.showCtaButtonsContainer}
              ctaButton1Text={fields?.ctaButton1Text}
              ctaButton1Url={fields?.ctaButton1Url}
              showCtaButton2={fields?.showCtaButton2}
              ctaButton2Text={fields?.ctaButton2Text}
              ctaButton2Url={fields?.ctaButton2Url}
              trackingKeyword={fields?.trackingKeyword}
            />
          </Box>
        </Box>
      </Box>
    </HeroProvider>
  );
};

type HeroFields = (ILpComponentHeroV2 | ILpComponentHeroV2WithMarkers)['fields'];

export const getHeroV2PreloadImages = (fields: HeroFields): PictureSourceSpec => {
  if (isTreatmentAnalyticsId(fields.analyticsId)) {
    return generatePresetPictureSetProps({
      ...HERO_FULLBLEED_IMAGE_PROPS,
      photoUrl: FULLBLEED_HERO_IMAGE_URL,
    }).sources;
  }

  return preloadHeroImages({
    imageDesktopUrl: fields.imageDesktopUrl,
    imageMobileUrl: fields.imageMobileUrl,
  });
};

const ControlHero: React.FC<{
  fields: HeroFields;
  customization?: HeroV2CustomizationProps;
  addressEntryRef: (node?: Element | null | undefined) => void;
}> = ({ fields, customization, addressEntryRef }) => {
  const router = useRouter();
  const headlineMarketIdentifier = getFirstQueryParam(router.query.headline_market_name);
  const headlineMarketDisplayName = getMarketDisplayNameForHeroV2(headlineMarketIdentifier);
  const displayMarketNameInHeadline =
    headlineMarketDisplayName &&
    'showMarketNameInHeadline' in fields &&
    fields.showMarketNameInHeadline &&
    !customization?.headline;
  const headline =
    customization?.headline !== undefined
      ? customization.headline
      : fields?.title + (displayMarketNameInHeadline ? ` in ${headlineMarketDisplayName}` : '');

  const hasBothSocialAndPress = fields?.showSocialSection && fields?.showPressSection;
  return (
    <Box
      width="90%"
      mx="auto"
      paddingHorizontal="$10"
      $largerThanSM={{
        paddingBottom: hasBothSocialAndPress ? '$16' : '$0',
      }}
    >
      <Box
        flexDirection="column"
        justifyContent="space-between"
        rowGap="$10"
        position="relative"
        $smallerThanSM={{ paddingTop: '$10' }}
        $largerThanSM={{ flexDirection: 'row', columnGap: '$8', rowGap: '$4' }}
      >
        <Box
          $largerThanSM={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '40%',
            flexShrink: 0,
          }}
        >
          <HeroTitleAndAddress
            analyticsId={fields?.analyticsId}
            title={headline}
            subtitle={fields?.subtitle}
            showAddressInput={fields?.showAddressInputCta}
            showSocialSection={fields?.showSocialSection}
            renderDesktopInput={customization?.renderDesktopInput}
            renderMobileInput={customization?.renderMobileInput}
            additionalAddressContent={customization?.additionalAddressContent}
            addressEntryRef={addressEntryRef}
            addressCtaText={fields?.addressCtaText}
            addressCtaTextSmall={fields?.addressCtaTextSmall}
            addressCtaPlaceholderText={fields?.addressCtaPlaceholderText}
            addressCtaPlaceholderTextSmall={fields?.addressCtaPlaceholderTextSmall}
            showCtaButtonsContainer={fields?.showCtaButtonsContainer}
            ctaButton1Text={fields?.ctaButton1Text}
            ctaButton1Url={fields?.ctaButton1Url}
            showCtaButton2={fields?.showCtaButton2}
            ctaButton2Text={fields?.ctaButton2Text}
            ctaButton2Url={fields?.ctaButton2Url}
            trackingKeyword={fields?.trackingKeyword}
          />
        </Box>
        <Box position="relative" $largerThanSM={{ width: '50%', flexShrink: 0 }}>
          <HeroImages
            imageDesktopUrl={fields?.imageDesktopUrl}
            imageMobileUrl={fields?.imageMobileUrl}
            lowBandwidth={customization?.lowBandwidth}
          />
          {customization?.children}
        </Box>
        {(fields?.showSocialSection || fields?.showPressSection) && (
          <Box
            id="desktop-press-box"
            position="absolute"
            left="50%"
            transform="translateX(-50%)"
            zIndex={1}
            $largerThanLG={{ bottom: 32 }}
            $largerThanSM={{ bottom: hasBothSocialAndPress ? -100 : -40 }}
            $smallerThanMD={{ position: 'relative' }}
          >
            {fields?.showSocialSection && (
              <Box
                $smallerThanMD={{ display: 'inherit' }}
                $smallerThanLG={{ display: 'none' }}
                $largerThanMD={{ display: 'inherit' }}
              >
                <SocialProofBox autoplayInterval={3000} />
              </Box>
            )}
            {fields?.showPressSection && (
              <Box marginTop="$8" $smallerThanMD={{ marginTop: 0 }}>
                <PressBox />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const RenderHeroV2 = (
  entry: ILpComponentHeroV2 | ILpComponentHeroV2WithMarkers,
  resolvedData?: Awaited<ReturnType<typeof heroV2Loader>>,
  customization?: HeroV2CustomizationProps,
) => {
  const router = useRouter();
  const { authentication } = useAuth();

  const { fields } = entry;
  const isFullBleedHero = isTreatmentAnalyticsId(fields.analyticsId);
  const hideStickyCta =
    customization?.hideStickyCtaOverride !== undefined
      ? Boolean(customization.hideStickyCtaOverride)
      : resolvedData?.stickyCTA === 'None' || authentication.state === 'authenticated';
  const headlineMarketIdentifier = getFirstQueryParam(router.query.headline_market_name);
  const headlineMarketDisplayName = getMarketDisplayNameForHeroV2(headlineMarketIdentifier);
  const displayMarketNameInHeadline =
    headlineMarketDisplayName &&
    'showMarketNameInHeadline' in fields &&
    fields.showMarketNameInHeadline &&
    !customization?.headline;
  const headline =
    customization?.headline !== undefined
      ? customization.headline
      : fields?.title + (displayMarketNameInHeadline ? ` in ${headlineMarketDisplayName}` : '');

  const { addressEntryRef, showStickyCTAUtility } = useStickyCTA();

  const variant = customization?.variant || 'default';
  const analyticsPrefix = `cosmos-landing-page-hero-v2${
    fields?.analyticsId ? `-${fields?.analyticsId}` : ''
  }`;

  if (isFullBleedHero) {
    return (
      <FullBleedHero
        fields={fields}
        headline={headline}
        customization={customization}
        analyticsPrefix={analyticsPrefix}
        hideStickyCta={hideStickyCta}
        showStickyCTAUtility={showStickyCTAUtility}
        addressEntryRef={addressEntryRef}
      />
    );
  }

  return (
    <HeroProvider variant={variant} analyticsPrefix={analyticsPrefix}>
      <Box
        analyticsName={
          customization?.analyticsName || `cosmos-landing-page-hero-v2-${fields?.analyticsId}`
        }
        trackImpression
        inViewThreshold={0.3}
      >
        {!hideStickyCta && (
          <StickyCTAUtility analyticsId={fields?.analyticsId} isVisible={showStickyCTAUtility} />
        )}
        <ControlHero
          fields={fields}
          customization={customization}
          addressEntryRef={addressEntryRef}
        />
      </Box>
    </HeroProvider>
  );
};

type IHeroV2LoaderReturn = {
  preloadImages: PictureSourceSpec;
  stickyCTA: string | undefined;
};

const heroV2Loader: LoaderFn<ILpComponentHeroV2, IHeroV2LoaderReturn> = async (
  input,
  root?: Entry<ILandingPageFields>,
) => {
  const preloadImages = getHeroV2PreloadImages(input.fields);
  const loaderReturn: IHeroV2LoaderReturn = {
    preloadImages,
    stickyCTA: getStickyCTAFromRoot(root),
  };
  return loaderReturn;
};

const HeroV2: EntryComponent<ILpComponentHeroV2, Awaited<ReturnType<typeof heroV2Loader>>> = {
  render: (entry, resolvedData) => RenderHeroV2(entry, resolvedData),
  loader: heroV2Loader,
};

export { RenderHeroV2 };
export default HeroV2;
